/*
  KP: "YOU MAY ALSO LIKE" on product pages: a row of products that go with the one being
  looked at, chosen by Shopify's own recommendations (the same ones Shopify's Search &
  Discovery app controls). It replaces the Rebuy app's product row ("Pairs well with...").

  Why: Rebuy's row put a hidden dropdown of every phone model and case type (about 266
  choices) on each of its four cards, which made up about half of everything on the page.
  This row is a photo, a name and a price per product, each one a link to that product,
  and it is only fetched when the shopper scrolls near it. The products are asked for
  through Shopify's Storefront API (the same way assets/kp-bundles.js asks for a case's
  matching items), which sends back just the name, photo and price of each: about 4 KB.
  (Shopify's older address for recommendations sends every variant of every product,
  about 2,000 KB for eight phone cases, so it is not used.)

  Where it goes (16 Oct 2026, after Rebuy was uninstalled, which took Rebuy's block out of
  the product layouts and left an empty "PAIRS WELL WITH..." heading): on EVERY product page,
  the first of these that the page has:
    1. Rebuy's old holder, if a layout still has one (from before the app was removed);
    2. the theme's "Apps" section headed "Pairs well with..." or "You may also like..."
       (now empty): the row goes inside it and its old heading is hidden (phone cases,
       AirPods, iPad, wallets, ring holders and the other case layouts; prints);
    3. otherwise a new section of the same kind straight after the product's own section
       (prints, mugs, clothing and every other layout).
  Nothing in the product templates had to change. If the empty "Apps" sections are ever
  deleted in the theme editor, the row simply falls back to 3.

  Settings are in snippets/theme-globals.liquid (window.KP_RECS): the title, how many
  products to ask for, and which Rebuy widget number to replace. Styles: kp-recs.css,
  which this file fetches itself, at the same time as the products.
  To go back to Rebuy's row, delete the kp-recs lines in theme-globals.liquid.
*/
(function () {
  'use strict';

  var cfg = window.KP_RECS;
  if (!cfg || !cfg.productId) return;

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var API = '/api/2025-07/graphql.json';
  var QUERY =
    'query ($id: ID!, $c: CountryCode) @inContext(country: $c) {' +
    ' recs: productRecommendations(productId: $id, intent: RELATED) {' +
    ' title handle availableForSale featuredImage { url }' +
    ' priceRange { minVariantPrice { amount currencyCode } maxVariantPrice { amount } }' +
    ' compareAtPriceRange { minVariantPrice { amount } } } }';

  function money(amount, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function sized(url, width) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function card(product) {
    var image = (product.featuredImage || {}).url || '';
    var low = product.priceRange.minVariantPrice;
    var price = parseFloat(low.amount);
    var top = parseFloat(product.priceRange.maxVariantPrice.amount);
    var was = parseFloat((product.compareAtPriceRange.minVariantPrice || {}).amount || 0);
    var onSale = was > price;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return (
      '<a class="kp-recs__card" href="' + escapeHtml(root + 'products/' + product.handle) + '">' +
      '<span class="kp-recs__media">' +
      (image
        ? '<img src="' + escapeHtml(sized(image, 360)) + '" srcset="' +
          escapeHtml(sized(image, 240)) + ' 240w, ' + escapeHtml(sized(image, 360)) + ' 360w, ' +
          escapeHtml(sized(image, 540)) + ' 540w" sizes="(min-width: 1200px) 15vw, (min-width: 720px) 22vw, 44vw" alt="" loading="lazy" width="360" height="360">'
        : '') +
      '</span>' +
      '<span class="kp-recs__name">' + escapeHtml(product.title) + '</span>' +
      '<span class="kp-recs__price' + (onSale ? ' kp-recs__price--sale' : '') + '">' +
      (top > price ? 'From ' : '') +
      money(price, low.currencyCode) +
      (onSale ? ' <s>' + money(was, low.currencyCode) + '</s>' : '') +
      '</span>' +
      '</a>'
    );
  }

  function addStyles() {
    if (!cfg.css || document.querySelector('link[data-kp-recs]')) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cfg.css;
    link.setAttribute('data-kp-recs', '');
    document.head.appendChild(link);
  }

  function load(box) {
    addStyles();
    fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: QUERY,
        variables: {
          id: 'gid://shopify/Product/' + cfg.productId,
          c: (window.Shopify && window.Shopify.country) || null
        }
      })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (json) {
        var html = '';
        var shown = 0;
        var products = (json && json.data && json.data.recs) || [];
        for (var i = 0; i < products.length && shown < (cfg.limit || 8); i++) {
          if (!products[i].availableForSale) continue;
          html += card(products[i]);
          shown++;
        }
        if (!html) {
          box.style.display = 'none';
          return;
        }
        box.innerHTML =
          '<h2 class="kp-recs__title">' + escapeHtml(cfg.title || 'You may also like') + '</h2>' +
          '<div class="kp-recs__row">' + html + '</div>';
      })
      .catch(function () {
        box.style.display = 'none';
      });
  }

  // Where the row goes (see "Where it goes" at the top).
  function place(box) {
    // 1. Rebuy's old holder.
    var holders = cfg.rebuyWidget ? document.querySelectorAll('[data-rebuy-id="' + cfg.rebuyWidget + '"]') : [];
    for (var i = 0; i < holders.length; i++) {
      if (holders[i].closest('#rebuy-cart')) continue;
      holders[i].parentNode.replaceChild(box, holders[i]);
      return true;
    }

    // 2. The theme's "Apps" section headed "Pairs well with..." or "You may also like...".
    var headings = document.querySelectorAll('.apps .section-header');
    for (var j = 0; j < headings.length; j++) {
      if (!/pairs\s+well|you\s+may\s+also\s+like/i.test(headings[j].textContent)) continue;
      var inner = headings[j].closest('.section-inner') || headings[j].parentNode;
      inner.appendChild(box);
      return true;
    }

    // 3. A new section straight after the product's own section.
    var main = document.querySelector('.main-product-section') || document.querySelector('[id^="shopify-section-"][id$="__main"]');
    if (!main || !main.parentNode) return false;
    var section = document.createElement('div');
    section.className = 'shopify-section kp-recs-section';
    section.innerHTML = '<div class="apps section"><div class="section-inner"></div></div>';
    section.querySelector('.section-inner').appendChild(box);
    main.parentNode.insertBefore(section, main.nextSibling);
    return true;
  }

  function takeOver() {
    if (document.querySelector('.kp-recs')) return;

    var box = document.createElement('section');
    box.className = 'kp-recs';
    box.setAttribute('aria-label', cfg.title || 'You may also like');
    // Keeps the place open so the page can tell when the shopper is getting near it.
    box.style.minHeight = '1px';
    if (!place(box)) return;

    // The theme's own title over the old row ("Pairs well with...") is hidden: this row
    // has its own title.
    var section = box.closest('.shopify-section');
    var oldTitle = section && section.querySelector('.section-header');
    if (oldTitle && !oldTitle.contains(box)) oldTitle.style.display = 'none';

    if ('IntersectionObserver' in window) {
      var watcher = new IntersectionObserver(
        function (entries) {
          for (var j = 0; j < entries.length; j++) {
            if (entries[j].isIntersecting) {
              watcher.disconnect();
              box.style.minHeight = '';
              load(box);
              return;
            }
          }
        },
        { rootMargin: '1200px 0px' }
      );
      watcher.observe(box);
    } else {
      box.style.minHeight = '';
      load(box);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', takeOver);
  } else {
    takeOver();
  }
})();
